import { IAuthRepository } from './IAuthRepository';
import { User } from '../models/User';
import { Session } from '../models/Session';
import { AuthToken } from '../value-objects/AuthToken';
import { LoginAttempt } from '../value-objects/LoginAttempt';
import { Email } from '../value-objects/Email';
import { Phone } from '../value-objects/Phone';
import { UserId } from '../value-objects/UserId';
import { SessionId } from '../value-objects/SessionId';
import { UserRole } from '../value-objects/UserRole';

/**
 * API 기반 Auth Repository 구현체
 */
export class AuthApiRepository implements IAuthRepository {
  private readonly baseURL: string;
  private readonly apiKey?: string;

  constructor(baseURL: string, apiKey?: string) {
    this.baseURL = baseURL.replace(/\/$/, ''); // 마지막 슬래시 제거
    this.apiKey = apiKey;
  }

  /**
   * HTTP 요청 헬퍼
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: any = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.apiKey) {
      headers['Authorization'] = `Bearer ${this.apiKey}`;
    }

    // 로컬 스토리지에서 토큰 가져오기
    const token = localStorage.getItem('auth_token');
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`
      );
    }

    return response.json();
  }

  // User 관련 메서드 구현
  async findUserByEmail(email: Email): Promise<User | null> {
    try {
      const data = await this.request<any>(`/users/email/${encodeURIComponent(email.value)}`);
      return this.mapToUser(data);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async findUserById(id: UserId): Promise<User | null> {
    try {
      const data = await this.request<any>(`/users/${id.value}`);
      return this.mapToUser(data);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async createUser(user: User): Promise<User> {
    const userData = {
      email: user.email.value,
      phone: user.phone.value,
      name: user.name,
      role: user.role.value,
      isActive: user.isActive,
      profileImageUrl: user.profileImageUrl,
    };

    const data = await this.request<any>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });

    return this.mapToUser(data);
  }

  async updateUser(user: User): Promise<User> {
    const userData = {
      email: user.email.value,
      phone: user.phone.value,
      name: user.name,
      role: user.role.value,
      isActive: user.isActive,
      profileImageUrl: user.profileImageUrl,
    };

    const data = await this.request<any>(`/users/${user.id.value}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });

    return this.mapToUser(data);
  }

  async deleteUser(id: UserId): Promise<void> {
    await this.request(`/users/${id.value}`, {
      method: 'DELETE',
    });
  }

  async findUsers(options: {
    page?: number;
    limit?: number;
    role?: string;
    isActive?: boolean;
  } = {}): Promise<{
    users: User[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.role) params.append('role', options.role);
    if (options.isActive !== undefined) params.append('isActive', options.isActive.toString());

    const data = await this.request<any>(`/users?${params.toString()}`);
    
    return {
      users: data.users.map((userData: any) => this.mapToUser(userData)),
      total: data.total,
      page: data.page,
      limit: data.limit,
    };
  }

  // Session 관련 메서드 구현
  async findSessionById(id: SessionId): Promise<Session | null> {
    try {
      const data = await this.request<any>(`/sessions/${id.value}`);
      return this.mapToSession(data);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async findActiveSessionsByUserId(userId: UserId): Promise<Session[]> {
    const data = await this.request<any>(`/users/${userId.value}/sessions/active`);
    return data.map((sessionData: any) => this.mapToSession(sessionData));
  }

  async createSession(session: Session): Promise<Session> {
    const sessionData = {
      userId: session.userId.value,
      ipAddress: session.ipAddress,
      userAgent: session.userAgent,
      expiresAt: session.expiresAt?.toISOString(),
    };

    const data = await this.request<any>('/sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });

    return this.mapToSession(data);
  }

  async updateSession(session: Session): Promise<Session> {
    const sessionData = {
      ipAddress: session.ipAddress,
      userAgent: session.userAgent,
      expiresAt: session.expiresAt?.toISOString(),
      lastAccessedAt: session.lastAccessedAt?.toISOString(),
    };

    const data = await this.request<any>(`/sessions/${session.id.value}`, {
      method: 'PUT',
      body: JSON.stringify(sessionData),
    });

    return this.mapToSession(data);
  }

  async deleteSession(id: SessionId): Promise<void> {
    await this.request(`/sessions/${id.value}`, {
      method: 'DELETE',
    });
  }

  async cleanupExpiredSessions(): Promise<number> {
    const data = await this.request<any>('/sessions/cleanup', {
      method: 'POST',
    });
    return data.deletedCount;
  }

  // 인증 관련 메서드 구현
  async authenticate(email: Email, password: string): Promise<{
    user: User;
    token: AuthToken;
    session: Session;
  } | null> {
    try {
      const data = await this.request<any>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: email.value,
          password,
        }),
      });

      const user = this.mapToUser(data.user);
      const token = AuthToken.fromJWT(data.accessToken, data.refreshToken);
      const session = this.mapToSession(data.session);

      return { user, token, session };
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        return null;
      }
      throw error;
    }
  }

  async findUserByToken(token: string): Promise<User | null> {
    try {
      const data = await this.request<any>('/api/v1/supabase-auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return this.mapToUser(data);
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        return null;
      }
      throw error;
    }
  }

  async refreshToken(refreshToken: string): Promise<{
    token: AuthToken;
    session: Session;
  } | null> {
    try {
      console.log('🔍 [DEBUG] refreshToken 요청 시작:', {
        endpoint: '/api/v1/supabase-auth/refresh',
        refreshToken: refreshToken?.substring(0, 20) + '...',
        bodyToSend: { refreshToken }
      });

      // ✅ DDD 원칙 유지: Infrastructure Layer만 수정
      // refresh 요청은 request 메서드를 우회하여 직접 fetch 호출
      // 이유: request 메서드가 자동으로 auth_token(access_token)을
      // Authorization 헤더에 추가하는데, refresh 요청에는 불필요함
      const url = `${this.baseURL}/api/v1/supabase-auth/refresh`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Authorization 헤더 의도적으로 제외
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [DEBUG] refreshToken 실패:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log('✅ [DEBUG] refreshToken 응답 받음:', {
        hasAccessToken: !!data.access_token,
        hasRefreshToken: !!data.refresh_token
      });

      const token = AuthToken.fromJWT(data.access_token, data.refresh_token);
      const session = this.mapToSession(data.session);

      return { token, session };
    } catch (error) {
      console.error('❌ [DEBUG] refreshToken 예외:', error);
      if (error instanceof Error && error.message.includes('401')) {
        return null;
      }
      throw error;
    }
  }

  async logout(sessionId: SessionId): Promise<void> {
    await this.request('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ sessionId: sessionId.value }),
    });
  }

  async logoutAllSessions(userId: UserId): Promise<void> {
    await this.request('/auth/logout-all', {
      method: 'POST',
      body: JSON.stringify({ userId: userId.value }),
    });
  }

  // 로그인 시도 관련 메서드
  async recordLoginAttempt(attempt: LoginAttempt): Promise<void> {
    await this.request('/auth/login-attempts', {
      method: 'POST',
      body: JSON.stringify({
        email: attempt.email,
        success: attempt.success,
        ipAddress: attempt.ipAddress,
        userAgent: attempt.userAgent,
        timestamp: attempt.timestamp.toISOString(),
      }),
    });
  }

  async findLoginAttemptsByEmail(email: Email, limit = 50): Promise<LoginAttempt[]> {
    const data = await this.request<any>(
      `/auth/login-attempts/email/${encodeURIComponent(email.value)}?limit=${limit}`
    );
    return data.map((attemptData: any) => this.mapToLoginAttempt(attemptData));
  }

  async findLoginAttemptsByIP(ipAddress: string, limit = 50): Promise<LoginAttempt[]> {
    const data = await this.request<any>(
      `/auth/login-attempts/ip/${encodeURIComponent(ipAddress)}?limit=${limit}`
    );
    return data.map((attemptData: any) => this.mapToLoginAttempt(attemptData));
  }

  async cleanupOldLoginAttempts(olderThanDays: number): Promise<number> {
    const data = await this.request<any>('/auth/login-attempts/cleanup', {
      method: 'POST',
      body: JSON.stringify({ olderThanDays }),
    });
    return data.deletedCount;
  }

  // 비밀번호 관련 메서드
  async changePassword(userId: UserId, newPasswordHash: string): Promise<void> {
    await this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        userId: userId.value,
        newPasswordHash,
      }),
    });
  }

  async createPasswordResetToken(email: Email): Promise<string | null> {
    try {
      const data = await this.request<any>('/auth/reset-password/request', {
        method: 'POST',
        body: JSON.stringify({ email: email.value }),
      });
      return data.resetToken;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async validatePasswordResetToken(token: string): Promise<UserId | null> {
    try {
      const data = await this.request<any>('/auth/reset-password/validate', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      return UserId.fromString(data.userId);
    } catch (error) {
      if (error instanceof Error && error.message.includes('400')) {
        return null;
      }
      throw error;
    }
  }

  async resetPassword(token: string, newPasswordHash: string): Promise<boolean> {
    try {
      await this.request('/auth/reset-password/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, newPasswordHash }),
      });
      return true;
    } catch (error) {
      if (error instanceof Error && error.message.includes('400')) {
        return false;
      }
      throw error;
    }
  }

  // 보안 관련 메서드
  async lockAccount(userId: UserId, lockUntil: Date): Promise<void> {
    await this.request('/auth/lock-account', {
      method: 'POST',
      body: JSON.stringify({
        userId: userId.value,
        lockUntil: lockUntil.toISOString(),
      }),
    });
  }

  async unlockAccount(userId: UserId): Promise<void> {
    await this.request('/auth/unlock-account', {
      method: 'POST',
      body: JSON.stringify({ userId: userId.value }),
    });
  }

  async setAccountActive(userId: UserId, isActive: boolean): Promise<void> {
    await this.request('/auth/set-active', {
      method: 'POST',
      body: JSON.stringify({
        userId: userId.value,
        isActive,
      }),
    });
  }

  // 감사 로그 관련 메서드
  async recordSecurityEvent(event: {
    userId?: UserId;
    eventType: string;
    description: string;
    ipAddress?: string;
    userAgent?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    await this.request('/auth/security-events', {
      method: 'POST',
      body: JSON.stringify({
        userId: event.userId?.value,
        eventType: event.eventType,
        description: event.description,
        ipAddress: event.ipAddress,
        userAgent: event.userAgent,
        metadata: event.metadata,
      }),
    });
  }

  async findSecurityEvents(options: {
    userId?: UserId;
    eventType?: string;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
  } = {}): Promise<Array<{
    id: string;
    userId?: string;
    eventType: string;
    description: string;
    ipAddress?: string;
    userAgent?: string;
    metadata?: Record<string, any>;
    createdAt: Date;
  }>> {
    const params = new URLSearchParams();
    if (options.userId) params.append('userId', options.userId.value);
    if (options.eventType) params.append('eventType', options.eventType);
    if (options.startDate) params.append('startDate', options.startDate.toISOString());
    if (options.endDate) params.append('endDate', options.endDate.toISOString());
    if (options.limit) params.append('limit', options.limit.toString());

    const data = await this.request<any>(`/auth/security-events?${params.toString()}`);
    
    return data.map((event: any) => ({
      ...event,
      createdAt: new Date(event.createdAt),
    }));
  }

  // 캐시 관련 메서드
  async invalidateUserCache(userId: UserId): Promise<void> {
    await this.request('/cache/users/invalidate', {
      method: 'POST',
      body: JSON.stringify({ userId: userId.value }),
    });
  }

  async invalidateSessionCache(sessionId: SessionId): Promise<void> {
    await this.request('/cache/sessions/invalidate', {
      method: 'POST',
      body: JSON.stringify({ sessionId: sessionId.value }),
    });
  }

  async invalidateAllAuthCache(): Promise<void> {
    await this.request('/cache/auth/invalidate-all', {
      method: 'POST',
    });
  }

  // 매핑 헬퍼 메서드들
  private mapToUser(data: any): User {
    return new User({
      id: UserId.fromString(data.id),
      email: Email.create(data.email),
      phone: Phone.create(data.phone),
      name: data.name,
      role: UserRole.create(data.role),
      isActive: data.isActive,
      lastLoginAt: data.lastLoginAt ? new Date(data.lastLoginAt) : undefined,
      profileImageUrl: data.profileImageUrl,
      failedLoginAttempts: data.failedLoginAttempts?.map((attempt: any) =>
        this.mapToLoginAttempt(attempt)
      ),
      accountLockedUntil: data.accountLockedUntil ? new Date(data.accountLockedUntil) : undefined,
      passwordChangedAt: data.passwordChangedAt ? new Date(data.passwordChangedAt) : undefined,
      organizationId: data.organization_id,
      organizationName: data.organization_name,
    });
  }

  private mapToSession(data: any): Session {
    return new Session({
      id: SessionId.fromString(data.id),
      userId: UserId.fromString(data.userId),
      token: data.token || '',
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : new Date(),
      lastAccessedAt: data.lastAccessedAt ? new Date(data.lastAccessedAt) : undefined,
      deviceXCircle: data.ipAddress || data.userAgent ? {
        ip: data.ipAddress || '',
        userAgent: data.userAgent || '',
        deviceType: data.deviceType
      } : undefined,
    });
  }

  private mapToLoginAttempt(data: any): LoginAttempt {
    return new LoginAttempt({
      email: data.email,
      timestamp: new Date(data.timestamp),
      success: data.success,
      ipAddress: data.ipAddress,
      userAgent: data.userAgent,
    });
  }
}